<template>
  <v-row>
    <v-col cols="12">
      <v-card class="dn-card" flat>
        <div class="dn-header pl-base-bg">
          <div class="dn-header-left">
            <v-icon color="#fff" size="18" class="mr-3">mdi-file-document-plus-outline</v-icon>
            <div>
              <div class="dn-title">Debit Note #{{ debitNote?.debitNoteNumber }}</div>
              <div class="dn-subtitle">
                Against Bill #{{ debitNote?.billNumber }} &middot; {{ formatDate(debitNote?.debitNoteDate) }}
              </div>
            </div>
          </div>

          <div class="dn-header-right">
            <v-chip
              :color="statusColor(debitNote?.status)"
              dark
              small
              class="text-uppercase font-weight-bold"
            >
              {{ debitNote?.status }}
            </v-chip>

            <v-btn v-if="!isViewOnly" small class="dn-edit-btn ml-2" :to="`/debit-notes/${debitNote?._id}/edit`">
              Edit
            </v-btn>

            <v-btn
              v-if="debitNote?.status === 'ISSUED' && !debitNote?.transactionId && !isViewOnly && remainingRefundable > 0.01"
              small
              class="dn-refund-btn ml-2"
              @click="openRefundDialog"
            >
              Record Refund
            </v-btn>

            <v-btn
              v-if="debitNote?.status === 'ISSUED' && !isViewOnly"
              small
              class="dn-cancel-btn ml-2"
              :disabled="!!debitNote?.transactionId"
              @click="confirmCancel = true"
            >
              Cancel Note
            </v-btn>
          </div>
        </div>

        <div class="pa-6">
          <v-alert v-if="debitNote?.transactionId" type="success" outlined dense border="left" class="mb-4">
            Refund of {{ formatAmount(debitNote.totalAmount) }} has been recorded against this debit note.
          </v-alert>

          <v-alert
            v-else-if="debitNote?.status === 'ISSUED' && remainingRefundable <= 0.01"
            type="info"
            outlined
            dense
            border="left"
            class="mb-4"
          >
            No refund can be recorded yet — no payment has been made to the vendor on this purchase, so there's no cash to receive back.
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <div class="dn-party-card">
                <div class="dn-party-label">Vendor</div>
                <div class="dn-party-name">{{ debitNote?.vendor?.name }}</div>
                <div class="dn-party-line" v-if="debitNote?.vendor?.address">
                  {{ debitNote?.vendor?.address }}
                </div>
                <div class="dn-party-line" v-if="debitNote?.vendor?.gstin">
                  GSTIN: {{ debitNote?.vendor?.gstin }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="dn-party-card">
                <div class="dn-party-label">Original Bill</div>
                <div class="dn-party-name">
                  <router-link :to="`/purchases/${debitNote?.purchaseId}/view`">
                    #{{ debitNote?.billNumber }}
                  </router-link>
                </div>
                <div class="dn-party-line">{{ formatDate(debitNote?.billDate) }}</div>
                <div class="dn-party-line">Reason: {{ reasonLabel(debitNote?.reason) }}</div>
              </div>
            </v-col>
          </v-row>

          <div class="dn-items-wrap mt-4">
            <div class="table-scroll">
              <v-simple-table dense class="dn-items-table">
                <thead>
                  <tr>
                    <th class="text-left">Description</th>
                    <th class="text-right">HSN / SAC</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Rate</th>
                    <th class="text-right">Tax %</th>
                    <th class="text-center">ITC Eligible</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in debitNote?.items" :key="i">
                    <td>{{ item.description }}</td>
                    <td class="text-right">{{ item.hsn }}</td>
                    <td class="text-right">{{ item.quantity }}</td>
                    <td class="text-right">{{ formatAmount(item.price) }}</td>
                    <td class="text-right">{{ item.taxPercent }}%</td>
                    <td class="text-center">{{ item.isItcEligible ? "Yes" : "No" }}</td>
                    <td class="text-right font-weight-medium">{{ formatAmount(item.amount) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </div>

          <v-row dense class="mt-4">
            <v-col cols="6" md="3">
              <div class="dn-itc-stat">
                <div class="dn-itc-stat-label">ITC Reversed</div>
                <div class="dn-itc-stat-value">
                  {{ formatAmount((debitNote?.itcReversedCgst || 0) + (debitNote?.itcReversedSgst || 0) + (debitNote?.itcReversedIgst || 0)) }}
                </div>
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="dn-itc-stat">
                <div class="dn-itc-stat-label">ITC Blocked (this note)</div>
                <div class="dn-itc-stat-value">
                  {{ formatAmount((debitNote?.itcBlockedCgst || 0) + (debitNote?.itcBlockedSgst || 0) + (debitNote?.itcBlockedIgst || 0)) }}
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row justify="end" class="mt-4">
            <v-col cols="12" md="4">
              <div class="dn-totals-card">
                <v-row dense>
                  <v-col class="grey--text py-1">Subtotal</v-col>
                  <v-col class="text-right py-1">{{ formatAmount(debitNote?.subtotal) }}</v-col>
                </v-row>

                <template v-if="debitNote?.isInterState">
                  <v-row dense>
                    <v-col class="grey--text py-1">IGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(debitNote?.igst) }}</v-col>
                  </v-row>
                </template>
                <template v-else>
                  <v-row dense>
                    <v-col class="grey--text py-1">CGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(debitNote?.cgst) }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col class="grey--text py-1">SGST</v-col>
                    <v-col class="text-right py-1">{{ formatAmount(debitNote?.sgst) }}</v-col>
                  </v-row>
                </template>

                <v-divider class="my-2" />

                <v-row dense class="text-h6 font-weight-bold">
                  <v-col>Total</v-col>
                  <v-col class="text-right dn-total-value">{{ formatAmount(debitNote?.totalAmount) }}</v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="debitNote?.note" class="mt-4">
            <v-col>
              <v-alert type="info" outlined dense>{{ debitNote.note }}</v-alert>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>

    <global-dialog
      v-if="confirmCancel"
      v-model="confirmCancel"
      :buttons="cancelButtons"
      title="Cancel Debit Note"
    >
      <p>Are you sure you want to cancel this debit note? The debited amount will be reversed on the original purchase.</p>
    </global-dialog>

    <!-- RECORD REFUND -->
    <global-dialog
      v-model="refundDialog"
      title="Record Refund"
      :buttons="refundDialogButtons"
    >
      <v-row dense>
        <v-col cols="12" class="mt-3">
          <v-alert type="info" dense text>
            Only record this once the refund has actually been received from the vendor — this creates a real cash-in entry.
          </v-alert>
        </v-col>
        <v-col cols="12">
          <v-text-field
            :value="formatAmount(debitNote?.totalAmount)"
            label="Refund Amount"
            outlined
            dense
            readonly
          />

          <v-select
            v-model="refundPaymentMethod"
            :items="paymentMethods"
            label="Payment Method"
            outlined
            dense
            required
            class="mt-4"
          />
        </v-col>
      </v-row>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "@/components/common/global-dialog.vue";

export default {
  name: "ViewDebitNote",
  components: { GlobalDialog },

  data() {
    return {
      debitNote: null,
      remainingRefundable: 0,
      confirmCancel: false,
      cancelling: false,
      refundDialog: false,
      refundPaymentMethod: "",
      refunding: false,

      paymentMethods: [
        "CASH",
        "BANK_TRANSFER",
        "UPI",
        "DEBIT_CARD",
        "CREDIT_CARD",
        "CHEQUE",
        "NET_BANKING",
        "WALLET",
        "OTHER",
      ],
    };
  },

  computed: {
    cancelButtons() {
      return [
        {
          text: "Back",
          action: () => (this.confirmCancel = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Cancel Note",
          action: this.doCancel,
          color: "error",
          disabled: this.cancelling,
          loading: this.cancelling,
        },
      ];
    },

    refundDialogButtons() {
      return [
        {
          text: "Cancel",
          action: () => (this.refundDialog = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Record Refund",
          action: this.doRecordRefund,
          color: "primary",
          disabled: !this.refundPaymentMethod || this.refunding,
          loading: this.refunding,
        },
      ];
    },
  },

  async created() {
    await this.fetchDebitNote();
  },

  methods: {
    async fetchDebitNote() {
      try {
        const id = this.$route.params.id;
        const res = await this.$store.dispatch("debitNotes/getDebitNoteById", id);
        this.debitNote = res.debitNote;
        this.remainingRefundable = res.remainingRefundable || 0;
      } catch (err) {
        this.$toast?.error("Failed to load debit note");
      }
    },

    async doCancel() {
      if (this.cancelling) return;
      this.cancelling = true;
      try {
        const res = await this.$store.dispatch("debitNotes/cancelDebitNote", this.debitNote._id);
        this.debitNote = res.debitNote;
        this.$toast?.success("Debit note cancelled");
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to cancel debit note");
      } finally {
        this.cancelling = false;
        this.confirmCancel = false;
      }
    },

    openRefundDialog() {
      this.refundPaymentMethod = "";
      this.refundDialog = true;
    },

    async doRecordRefund() {
      if (this.refunding) return;
      this.refunding = true;
      try {
        const res = await this.$store.dispatch("debitNotes/recordRefund", {
          id: this.debitNote._id,
          paymentMethod: this.refundPaymentMethod,
        });
        this.debitNote = res.debitNote;
        this.$toast?.success("Refund recorded");
      } catch (err) {
        this.$toast?.error(err?.response?.data?.message || "Failed to record refund");
      } finally {
        this.refunding = false;
        this.refundDialog = false;
      }
    },

    reasonLabel(reason) {
      const map = {
        PURCHASE_RETURN: "Purchase Return",
        PRICE_CORRECTION: "Price Correction",
        OTHER: "Other",
      };
      return map[reason] || reason || "-";
    },

    statusColor(status) {
      switch (status) {
        case "ISSUED":
          return "green";
        case "CANCELLED":
          return "red";
        default:
          return "grey";
      }
    },

    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },
  },
};
</script>

<style scoped>
.dn-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.dn-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
}

.dn-header-left {
  display: flex;
  align-items: center;
}

.dn-header-right {
  display: flex;
  align-items: center;
}

.dn-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.dn-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.dn-edit-btn,
.dn-cancel-btn,
.dn-refund-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.dn-party-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
  height: 100%;
}

.dn-party-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 6px;
}

.dn-party-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.dn-party-line {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.dn-items-wrap {
  border: 1px solid #eef1f5;
  border-radius: 0;
  overflow: auto;
}

.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.dn-items-table {
  min-width: 640px;
}

.dn-items-table >>> th {
  background: #f8fafc;
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b !important;
}

.dn-itc-stat {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px;
}

.dn-itc-stat-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dn-itc-stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 4px;
}

.dn-totals-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 18px;
}

.dn-total-value {
  color: #0a5a90;
}
</style>
